import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal bloquant affiché à la première connexion (must_change_password=true).
 * L'utilisateur doit changer son mot de passe généré avant de continuer.
 */
export default function ChangePasswordModal() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const forced = !!user?.must_change_password;
  if (!forced) return null;

  const submit = async () => {
    if (next.length < 6) {
      toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (next !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Mot de passe mis à jour — bienvenue !");
      setCurrent(""); setNext(""); setConfirm("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={forced}>
      <DialogContent
        data-testid="force-change-password-dialog"
        className="max-w-md"
        // On empêche la fermeture par clic extérieur / touche Échap tant que non changé
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-14 h-14 mx-auto rounded-full bg-[#E4EBE1] flex items-center justify-center mb-2">
            <ShieldCheck className="w-7 h-7 text-[#2C4C3B]" />
          </div>
          <DialogTitle className="font-display text-2xl text-[#1A2B27] text-center">
            Bienvenue ! Change ton mot de passe
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-[#6b7f79]">
            C'est ta première connexion. Choisis un mot de passe personnel — il remplacera celui reçu par email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-tiny">Mot de passe reçu par email</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="fcp-current"
              autoFocus
              placeholder="Celui du mail de bienvenue"
              className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
            />
          </div>
          <div>
            <Label className="label-tiny">Nouveau mot de passe (min. 6 caractères)</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="fcp-new"
              className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
            />
          </div>
          <div>
            <Label className="label-tiny">Confirme le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="fcp-confirm"
              className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
            />
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={busy || !current || !next || !confirm}
          data-testid="fcp-submit"
          className="w-full mt-4 rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] h-11"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          {busy ? "Mise à jour…" : "Enregistrer et continuer"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
